import React from 'react';
import { HashRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import NavBar from './components/NavBar';
import Home from './pages/Home';
import Learn from './pages/Learn';
import ScenarioDojo from './pages/ScenarioDojo';
import VibeReader from './pages/VibeReader';
import Assessment from './pages/Assessment';
import Leaderboard from './pages/Leaderboard';
import Ikigai from './pages/Ikigai';
import IkigaiSpark from './pages/IkigaiSpark';
import IkigaiLab from './pages/IkigaiLab';
import ZenMonkey from './pages/ZenMonkey';
import ZenKoanPage from './pages/ZenKoan';
import ZenGardenPage from './pages/ZenGarden';
import IchigoIchie from './pages/IchigoIchie';
import IchigoTeaRoom from './pages/IchigoTeaRoom';
import IchigoMoments from './pages/IchigoMoments';
import LettingGo from './pages/LettingGo';
import LettingGoCeremony from './pages/LettingGoCeremony';
import LettingGoFlow from './pages/LettingGoFlow';
import Courage from './pages/Courage';
import CourageSeparation from './pages/CourageSeparation';
import CourageShift from './pages/CourageShift';
import Happy from './pages/Happy';
import HappyLove from './pages/HappyLove';
import HappyReliance from './pages/HappyReliance';
import Kaizen from './pages/Kaizen';
import KaizenSmallSteps from './pages/KaizenSmallSteps';
import KaizenHansei from './pages/KaizenHansei';
import Stoic from './pages/Stoic';
import StoicControl from './pages/StoicControl';
import StoicMorning from './pages/StoicMorning';
import SlowDown from './pages/SlowDown';
import SlowDownPause from './pages/SlowDownPause';
import SlowDownConnect from './pages/SlowDownConnect';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import Splash from './pages/Splash';
import { UserProvider, useUser } from './context/UserContext';
import { Shield } from 'lucide-react';

const AppContent: React.FC = () => {
  const { user, loading, isAdmin } = useUser();

  if (loading) {
    return <Splash />; 
  }

  // If not logged in OR logged in but not approved, show Splash
  if (!user.isApproved) {
    return <Splash />;
  }

  return (
    <Router>
        <div className="min-h-screen bg-paper font-sans text-nunchi-900 selection:bg-nunchi-200 flex flex-col">
          <div className="max-w-4xl w-full mx-auto pt-6 px-4 flex-grow">
            <NavBar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/learn" element={<Learn />} />
              
              <Route path="/ikigai" element={<Ikigai />} />
              <Route path="/ikigai/spark" element={<IkigaiSpark />} />
              <Route path="/ikigai/lab" element={<IkigaiLab />} />
              
              <Route path="/zen-monkey" element={<ZenMonkey />} />
              <Route path="/zen-monkey/koan" element={<ZenKoanPage />} />
              <Route path="/zen-monkey/garden" element={<ZenGardenPage />} />
              
              <Route path="/ichigo-ichie" element={<IchigoIchie />} />
              <Route path="/ichigo-ichie/tea-room" element={<IchigoTeaRoom />} />
              <Route path="/ichigo-ichie/moments" element={<IchigoMoments />} />
              
              <Route path="/letting-go" element={<LettingGo />} />
              <Route path="/letting-go/ceremony" element={<LettingGoCeremony />} />
              <Route path="/letting-go/flow" element={<LettingGoFlow />} />

              <Route path="/courage" element={<Courage />} />
              <Route path="/courage/separation" element={<CourageSeparation />} />
              <Route path="/courage/shift" element={<CourageShift />} />

              <Route path="/happy" element={<Happy />} />
              <Route path="/happy/love" element={<HappyLove />} />
              <Route path="/happy/reliance" element={<HappyReliance />} />

              <Route path="/kaizen" element={<Kaizen />} />
              <Route path="/kaizen/steps" element={<KaizenSmallSteps />} />
              <Route path="/kaizen/hansei" element={<KaizenHansei />} />

              <Route path="/stoic" element={<Stoic />} />
              <Route path="/stoic/control" element={<StoicControl />} />
              <Route path="/stoic/morning" element={<StoicMorning />} />

              <Route path="/slow" element={<SlowDown />} />
              <Route path="/slow/pause" element={<SlowDownPause />} />
              <Route path="/slow/connect" element={<SlowDownConnect />} />

              <Route path="/scenario" element={<ScenarioDojo />} />
              <Route path="/assessment" element={<Assessment />} />
              <Route path="/vibe-check" element={<VibeReader />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/profile" element={<Profile />} />
              
              {/* Admin Route Protected */}
              <Route path="/admin" element={isAdmin ? <AdminDashboard /> : <Navigate to="/" replace />} />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
            
          <footer className="py-8 text-center text-nunchi-400 text-xs mt-auto border-t border-nunchi-100/50 bg-white/50">
            <div className="max-w-4xl mx-auto flex justify-between items-center px-6">
              <p>© {new Date().getFullYear()} Nunchi Master. The art of listening.</p>
              {isAdmin && (
                <Link to="/admin" className="text-nunchi-300 hover:text-nunchi-600 transition-colors flex items-center gap-1">
                   <Shield size={10} /> Admin
                </Link>
              )}
            </div>
          </footer>
        </div>
      </Router>
  );
};

const App: React.FC = () => {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
};

export default App;